/**
 * Type definitions for Parlant API entities
 * These types are based on the parlant-client library and backend API
 */

/**
 * Message/Event source types
 * Identifies who or what generated the event
 */
export type MessageSource =
  | 'customer'
  | 'customer_ui'
  | 'ai_agent'
  | 'human_agent'
  | 'human_agent_on_behalf_of_ai_agent'
  | 'system';

/**
 * Event kind types
 * Represents different types of interactions in a conversation
 */
export type EventKind = 'message' | 'tool' | 'status' | 'custom';

/**
 * Status types for session status events
 */
export type StatusType =
  | 'ready'
  | 'processing'
  | 'typing'
  | 'error'
  | 'acknowledged'
  | 'cancelled';

/**
 * Session mode - determines if agent auto-responds
 */
export type SessionMode = 'auto' | 'manual';

/**
 * Content moderation levels
 */
export type ModerationLevel = 'auto' | 'paranoid' | 'none';

/**
 * Parlant customer entity
 */
export interface ParlantCustomer {
    id: string;
    name: string;
    tags?: string[];
    created_at?: string;
    updated_at?: string;
}

/**
 * Consumption offsets for tracking message read state
 */
export interface ConsumptionOffsets {
    client: number;
}

/**
 * Parlant session entity
 */
export interface ParlantSession {
    id: string;
    agent_id: string;
    customer_id?: string;
    title: string;
    created_at: string;
    updated_at: string;
    mode: SessionMode;
    consumption_offsets: ConsumptionOffsets;
    /** Arbitrary key-value metadata attached to the session. */
    metadata?: Record<string, unknown>;
    /** Labels attached to the session. */
    labels?: string[];
}

/**
 * Participant in an event
 */
export interface EventParticipant {
    display_name?: string;
    id?: string;
}

/**
 * Message event data
 *
 * When the backend streams a message token-by-token the `chunks` array grows with
 * each token.  The stream is complete when `chunks[chunks.length - 1] === null`.
 * `message` holds the fully-assembled text once the stream completes; during
 * streaming it may be empty — use `extractStreamingMessage()` to get the
 * best available text at any point in time.
 *
 * An `_interrupted` flag is set by the client transport when a per-message SSE
 * connection closes before the null terminator arrives.
 */
export interface MessageEventData {
    message: string;
    participant?: EventParticipant;
    flagged?: boolean;
    tags?: string[];
    canned_responses?: string[];
    /** Token chunks for streaming messages.  null at end = stream complete. */
    chunks?: (string | null)[];
    /** Set by transport when stream closed without null terminator. */
    _interrupted?: boolean;
}

/**
 * Status event data
 */
export interface StatusEventData {
    status: StatusType;
    message?: string;
    data?: Record<string, unknown>;
}

/**
 * Tool call result data
 */
export interface ToolResultData {
    data: unknown;
    metadata?: Record<string, unknown>;
    control?: Record<string, unknown>;
    canned_responses?: string[];
    canned_response_fields?: Record<string, unknown>;
}

/**
 * Individual tool call within a tool event
 */
export interface ToolCallData {
    tool_id: string;
    arguments: Record<string, unknown>;
    result?: ToolResultData;
}

/**
 * Tool event data - contains array of tool calls
 */
export interface ToolEventData {
    tool_calls: ToolCallData[];
}

/**
 * Generic event data (union of all possible event data types)
 */
export type EventData = MessageEventData | StatusEventData | ToolEventData | Record<string, unknown>;

/**
 * Parlant event entity
 * 
 * Note: `correlation_id` is used to group related events together (equivalent to
 * `trace_id` in some documentation). Events with the same correlation_id base
 * (before the '::' separator) belong to the same logical conversation turn.
 */
export interface ParlantEvent {
    id?: string;
    kind: EventKind;
    source: MessageSource;
    offset: number;
    /** Groups related events together (e.g., message + status events for the same turn) */
    correlation_id?: string;
    /** Upstream trace identifier (often mirrors correlation_id) */
    trace_id?: string;
    creation_utc: string;
    data: EventData;
    /** Event-level metadata from the Parlant API */
    metadata?: Record<string, unknown>;
    deleted?: boolean;
}

/**
 * Session creation parameters
 */
export interface SessionCreationParams {
    agentId: string;
    customerId?: string;
    title?: string;
    /** Whether to allow the agent to send an initial greeting (query param upstream) */
    allowGreeting?: boolean;
    /** Arbitrary key-value metadata to attach to the new session. */
    metadata?: Record<string, unknown>;
    /** Labels to attach to the new session. */
    labels?: string[];
}

/**
 * Event creation parameters for messages
 */
export interface MessageCreationParams {
    kind: 'message';
    source: MessageSource;
    message: string;
    /** Optional event metadata */
    metadata?: Record<string, unknown>;
    /** Optional structured payload */
    data?: unknown;
}

/**
 * Type guard to check if event data is MessageEventData
 */
export function isMessageEventData(data: EventData): data is MessageEventData {
    return 'message' in data;
}

/**
 * Type guard to check if event data is StatusEventData
 */
export function isStatusEventData(data: EventData): data is StatusEventData {
    return 'status' in data;
}

/**
 * Type guard to check if event data is ToolEventData
 */
export function isToolEventData(data: EventData): data is ToolEventData {
    return 'tool_calls' in data && Array.isArray((data as ToolEventData).tool_calls);
}

/**
 * Safely extract message from event data
 */
export function extractMessage(data: EventData): string {
    if (isMessageEventData(data)) {
        return data.message;
    }
    return '';
}

/**
 * Safely extract status from event data
 */
export function extractStatus(data: EventData): StatusType {
    if (isStatusEventData(data)) {
        return data.status;
    }
    return 'ready';
}

/**
 * Safely extract participant from event data
 */
export function extractParticipant(data: EventData): EventParticipant | undefined {
    if (isMessageEventData(data)) {
        return data.participant;
    }
    return undefined;
}

/**
 * Safely check if message is flagged by moderation
 */
export function isMessageFlagged(data: EventData): boolean {
    if (isMessageEventData(data)) {
        return data.flagged === true;
    }
    return false;
}

/**
 * Safely extract tags from message event data
 */
export function extractTags(data: EventData): string[] {
    if (isMessageEventData(data)) {
        return data.tags ?? [];
    }
    return [];
}

/**
 * Safely extract canned response IDs from message event data
 */
export function extractCannedResponses(data: EventData): string[] {
    if (isMessageEventData(data)) {
        return data.canned_responses ?? [];
    }
    return [];
}

/**
 * Safely extract tool IDs from tool event data
 */
export function extractToolIds(data: EventData): string[] {
    if (isToolEventData(data)) {
        return data.tool_calls.map(tc => tc.tool_id);
    }
    return [];
}

/**
 * Safely extract all tool calls from tool event data
 */
export function extractToolCalls(data: EventData): ToolCallData[] {
    if (isToolEventData(data)) {
        return data.tool_calls;
    }
    return [];
}

// ---------------------------------------------------------------------------
// Streaming helpers
// ---------------------------------------------------------------------------

/**
 * Returns true when a message event is actively streaming token chunks.
 *
 * A message is streaming when:
 *  - `chunks` is present (the backend is in streaming mode for this event), AND
 *  - either no chunks have arrived yet, OR the last chunk is not the null terminator.
 */
export function isMessageStreaming(data: EventData): boolean {
    if (!isMessageEventData(data)) return false;
    const { chunks } = data;
    if (chunks === undefined) return false;
    if (chunks.length === 0) return true;
    return chunks[chunks.length - 1] !== null;
}

/**
 * Returns the best available visible text for a (possibly streaming) message.
 *
 * - While streaming, joins all non-null chunks.
 * - After streaming completes (or for non-streaming messages), returns `data.message`.
 * - Falls back to an empty string so callers never receive undefined/null.
 */
export function extractStreamingMessage(data: EventData): string {
    if (!isMessageEventData(data)) return '';
    const { chunks, message } = data;

    if (chunks !== undefined && chunks.length > 0) {
        // Join non-null chunks to build partial/complete streamed text
        const assembled = chunks.filter((c): c is string => c !== null).join('');
        if (assembled.length > 0) return assembled;
    }

    return message ?? '';
}

/**
 * Safely extract status stage data from status event
 */
export function extractStatusStage(data: EventData): Record<string, unknown> | undefined {
    if (isStatusEventData(data) && data.data) {
        return data.data;
    }
    return undefined;
}

